/**
 * Creator-facing copy for a file dropped on the Studio front door.
 *
 * Every sentence here exists because the alternative was silence: the composer
 * used to navigate away from ANY drop without naming the file, so an
 * unsupported source looked exactly like an accepted one. These strings name
 * the file, name what is accepted, and never mention an internal error class.
 */

export interface SourceDropCopy {
  /** Heading for a drop we cannot forward. */
  rejectedTitle: string;
  /** Body for a rejected FILE — receives the file name. */
  rejectedFile: (fileName: string) => string;
  /** Body for a drag that carried no file at all. */
  rejectedNoFile: string;
  /** The accepted list, appended to either body. */
  accepted: (extensions: string) => string;
  /** Shown when a multi-file drop was reduced to its first file. */
  onlyFirstFile: (fileName: string, count: number) => string;
}

export function getSourceDropCopy(locale: string): SourceDropCopy {
  switch (locale) {
    case "ko":
      return {
        rejectedTitle: "이 파일은 여기서 열 수 없습니다",
        rejectedFile: (fileName) => `${fileName} 은(는) 지원하지 않는 형식입니다.`,
        rejectedNoFile: "끌어놓은 항목에 파일이 없습니다.",
        accepted: (extensions) => `지원 형식: ${extensions}`,
        onlyFirstFile: (fileName, count) =>
          `파일 ${count}개 중 첫 번째(${fileName})만 가져왔습니다.`,
      };
    case "ja":
      return {
        rejectedTitle: "このファイルはここでは開けません",
        rejectedFile: (fileName) => `${fileName} は対応していない形式です。`,
        rejectedNoFile: "ドロップされた項目にファイルがありません。",
        accepted: (extensions) => `対応形式: ${extensions}`,
        onlyFirstFile: (fileName, count) =>
          `${count} 件のうち最初の 1 件（${fileName}）のみ取り込みました。`,
      };
    case "zh":
      return {
        rejectedTitle: "无法在此处打开该文件",
        rejectedFile: (fileName) => `${fileName} 的格式不受支持。`,
        rejectedNoFile: "拖放的内容中没有文件。",
        accepted: (extensions) => `支持的格式：${extensions}`,
        onlyFirstFile: (fileName, count) =>
          `${count} 个文件中只导入了第一个（${fileName}）。`,
      };
    case "zh-TW":
      return {
        rejectedTitle: "無法在此開啟這個檔案",
        rejectedFile: (fileName) => `${fileName} 的格式不受支援。`,
        rejectedNoFile: "拖放的內容中沒有檔案。",
        accepted: (extensions) => `支援的格式：${extensions}`,
        onlyFirstFile: (fileName, count) =>
          `${count} 個檔案中只匯入了第一個（${fileName}）。`,
      };
    default:
      return {
        rejectedTitle: "This file cannot be opened here",
        rejectedFile: (fileName) => `${fileName} is not a supported format.`,
        rejectedNoFile: "That drag carried no file.",
        accepted: (extensions) => `Supported: ${extensions}`,
        onlyFirstFile: (fileName, count) =>
          `Only the first of ${count} files was taken (${fileName}).`,
      };
  }
}
